import { Injectable, WritableSignal, signal } from '@angular/core';
import { Router } from '@angular/router';
import { LogApi } from 'api/log/log-api.js';
import { ILoggedItems } from 'lib/cosmos/log/logService.js';
import { ErrorData } from 'lib/helpers/api-error-helper.class.js';
import { consoleHelper } from 'lib/helpers/console-helper.class.js';
import { toSha256Async } from 'lib/helpers/crypto-helper.js';
import { PlatformService } from 'lib/platform/index.js';
import { Observable, firstValueFrom, from } from 'rxjs';
import { HttpService } from 'src/app/http/http.service.js';
import { LogDb } from '../cosmos/log/logDb.js';

type ILogData = {
	message: string;
	email: string | undefined;
	stacktrace: string | undefined;
	behavior: LogDb.LogBehavior | undefined;
};

@Injectable({
	providedIn: 'root'
})
export class AppLogService {
	public readonly latestLog: WritableSignal<LogDb.ILogItem | undefined> = signal(undefined);
	public readonly latestError: WritableSignal<ErrorData | undefined> = signal(undefined);

	private readonly identicalLogItemsMax: number = 2;

	/**
	 * This is to prevent overloading server with identical logs
	 */
	private readonly loggedItems: ILoggedItems = {};

	constructor(
		private readonly httpService: HttpService,
		private readonly platformService: PlatformService,
		private readonly router: Router
	) {}

	async logErrorAsync(data: ILogData): Promise<void> {
		return await this.logItemAsync(data, 'error');
	}

	async logEventAsync(data: ILogData): Promise<void> {
		return await this.logItemAsync(data, 'event');
	}

	private async logItemAsync(data: ILogData, type: 'error' | 'event'): Promise<void> {
		if (!(await this.canLogItemAsync(data))) {
			consoleHelper.logToConsole({
				type: 'Warning',
				partA: 'Skipping log to db',
				message: data.message,
				data: data.stacktrace
			});
			return;
		}

		const response = await firstValueFrom(
			this.httpService.post<LogDb.ILogItem, LogApi.ILogEventRequest>((builder) =>
				builder
					.withCustomApiRoute('log', type === 'event' ? 'logEvent' : 'logError')
					.withData({
						message: data.message,
						email: data.email,
						behavior: data.behavior,
						url: this.getCurrentUrl(),
						stacktrace: data.stacktrace
					})
					.build()
			)
		);

		this.latestLog.set(response.data);
	}

	logEvent(data: ILogData): Observable<void> {
		return from(this.logErrorAsync(data));
	}

	logError(data: ILogData): Observable<void> {
		return from(this.logErrorAsync(data));
	}

	private getCurrentUrl(): string {
		// we are not using router intentionally because we need url visible to user
		if (this.platformService.isBrowser) {
			return window.location.pathname;
		}

		return this.router.url;
	}

	private async canLogItemAsync(logData: ILogData): Promise<boolean> {
		const logHash = await this.getItemHashAsync(logData);

		const existingLogRecord = this.loggedItems[logHash];

		if (existingLogRecord) {
			const newCount = existingLogRecord + 1;
			this.loggedItems[logHash] = newCount;

			if (newCount > this.identicalLogItemsMax) {
				return false;
			}
			return true;
		}

		this.loggedItems[logHash] = 1;
		return true;
	}

	private async getItemHashAsync(logData: ILogData): Promise<string> {
		return toSha256Async(`${logData.message}_${logData.email}_${logData.stacktrace}`);
	}
}
